import React from "react";

export type Rect = Pick<
  DOMRectReadOnly,
  "x" | "y" | "top" | "left" | "right" | "bottom" | "height" | "width"
>;

export const defaultRect: Rect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

export type StickyContextValue = {
  isStuck: boolean;
  stickyElementRef: (ref: HTMLElement) => void;
  stickyElement: HTMLElement;
  update: () => void;
};

export const StickyContext = React.createContext({} as any);

export const useStickyContext = () =>
  React.useContext<StickyContextValue>(StickyContext);

export const StickyBoundary = ({ children, container = document, buffer }) => {
  const stickyBuffer = buffer;
  const topRef = React.useRef<HTMLDivElement>(null);
  const bottomRef = React.useRef<HTMLDivElement>(null);
  const [stickyElement, stickyElementRef] = React.useState<HTMLElement>(null);
  const [stickyElementRect, setStickyElementRect] =
    React.useState<Rect>(defaultRect);

  const [isStuck, setIsStuck] = React.useState(false);

  const resizeObserver = React.useMemo(
    () =>
      new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { x, y, width, height, top, left, bottom, right } =
            entry.contentRect;
          setStickyElementRect({
            x,
            y,
            width,
            height,
            top,
            left,
            bottom,
            right,
          });
        }
      }),
    []
  );

  React.useEffect(() => {
    if (!stickyElement) return;
    resizeObserver.observe(stickyElement);
    return () => {
      resizeObserver.disconnect();
    };
  }, [stickyElement]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const targetInfo = entry.boundingClientRect;
          const rootBoundsInfo = entry.rootBounds;

          if (targetInfo.bottom < rootBoundsInfo.top) {
            setIsStuck(true);
          }

          if (
            targetInfo.bottom >= rootBoundsInfo.top &&
            targetInfo.bottom < rootBoundsInfo.bottom
          ) {
            setIsStuck(false);
          }
        }
      },
      {
        threshold: [0],
        root: container,
      }
    );

    observer.observe(topRef.current);

    return () => {
      observer.disconnect();
    };
  }, [container]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!stickyElement) return;
          const stickyInfo = stickyElement.getBoundingClientRect();
          const targetInfo = entry.boundingClientRect;
          const rootBoundsInfo = entry.rootBounds;
          const intersectionRatio = entry.intersectionRatio;

          if (
            targetInfo.bottom > rootBoundsInfo.top &&
            intersectionRatio === 1 &&
            stickyInfo.y === 0
          ) {
            setIsStuck(true);
          }

          if (
            targetInfo.top < rootBoundsInfo.top &&
            targetInfo.bottom < rootBoundsInfo.bottom
          ) {
            setIsStuck(false);
          }
        }
      },
      {
        threshold: [1],
        root: container,
      }
    );

    observer.observe(bottomRef.current);

    return () => {
      observer.disconnect();
    };
  }, [container, stickyElement]);

  return (
    <StickyContext.Provider
      value={{
        isStuck,
        stickyElementRef,
        stickyElement
      }}
    >
      <div
        style={{
          position: "relative",
        }}
      >
        <div
          ref={topRef}
          style={{
            position: "absolute",
            top: stickyBuffer,
            left: 0,
            right: 0,
            visibility: "hidden",
            display: 'block'
          }}
        />
        {children}
        <div
          ref={bottomRef}
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: stickyElementRect.height,
            visibility: "hidden",
            display: 'block'
          }}
        />
      </div>
    </StickyContext.Provider>
  );
};
